import CCColors from '@/Utils/CCColors';
import {Platform, StyleSheet} from 'react-native';

// ── Refined Classic design tokens, mapped onto the real CCColors palette ──
export const T = {
  yellow: CCColors.Primary.Default, //     #FACF47
  yellowDark: '#D1AB41',
  yellowLight: CCColors.Primary.lightYellow, // #FEF2B5
  brown: CCColors.Primary.Brown, //        #4D3F37
  cream: CCColors.Primary.lightBrown, //   #FAF6EB
  cream3: CCColors.LightBrownShade, //     #FDF1C8
  orange: CCColors.Red, //                 #E17846
  aqua: CCColors.Green, //                 #3DAB9E
  aquaDark: '#2E857B',
  purple: CCColors.Purple, //              #937ADB
  purpleDark: '#6F57B8',
  wa: '#25D366',
  waDark: '#1DA851',
  ops: CCColors.Orange, //                 #E17B21
  opsDark: '#B85F16',
  white: CCColors.White,
  textDark: CCColors.TextColor.Dark, //     #262322
  text: CCColors.TextColor.Primary, //      #4D3F37
  textGrey: CCColors.TextColor.GreyText, // #888280
  textMuted: CCColors.TextColor.Grey, //    #A8A7A6
  borderLight: CCColors.BorderLight, //     rgba(38,35,34,.10)
};

const shadow = (web: string, elevation = 4) =>
  Platform.select({
    web: {boxShadow: web},
    android: {elevation},
    ios: {
      shadowColor: '#4D3F37',
      shadowOffset: {width: 0, height: 2},
      shadowOpacity: 0.12,
      shadowRadius: 8,
    },
  }) as object;

export const shadows = {
  soft: '0 1px 3px rgba(77,63,55,0.08)',
  card: '0 2px 10px rgba(77,63,55,0.10)',
  pop: '0 8px 24px rgba(77,63,55,0.12)',
};

export const styles = StyleSheet.create({
  shadowSoft: shadow(shadows.soft, 2),
  shadowCard: shadow(shadows.card, 4),
  shadowPop: shadow(shadows.pop, 8),

  // ── Panel ──
  panel: {
    flex: 1,
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: CCColors.Primary.lightBrown, // cream
  },
  closeBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 5,
    width: 34,
    height: 34,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.85)',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // ── Hero ──
  hero: {position: 'relative', height: 300, width: '100%', overflow: 'hidden'},
  heroBoard: {
    position: 'absolute',
    top: 0,
    left: '-20%',
    width: '140%',
    height: '100%',
    backgroundImage: `repeating-conic-gradient(${CCColors.Primary.Default} 0% 25%, ${CCColors.Primary.Brown} 0% 50%)`,
    backgroundSize: '34px 34px',
    // Trapezoid ends higher (72% instead of 100%) and fades out by then, so the
    // "Hello, I'm Caissa!" heading below it sits on clean cream, not the busy
    // checkerboard.
    clipPath: 'polygon(0% 0%,100% 0%,72% 72%,28% 72%)',
    WebkitMaskImage: 'linear-gradient(to bottom, black 45%, transparent 72%)',
    maskImage: 'linear-gradient(to bottom, black 45%, transparent 72%)',
    opacity: 0.9,
  } as object,
  heroContent: {
    position: 'relative',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'flex-end', // sit content toward the bottom (shifted down)
    paddingBottom: 14,
    gap: 4,
  },
  heroAvatar: {width: 240, height: 200},

  // ── Body padding ──
  body: {paddingHorizontal: 28, paddingBottom: 20},

  // ── Picker ──
  pickerPrompt: {textAlign: 'center', marginBottom: 18},
  // Flexbox row (not grid): align-items:stretch makes both cards equal height.
  pickerRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
    marginBottom: 16,
  },
  pickerCardTop: {
    position: 'relative',
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRadius: 18,
    paddingVertical: 20,
    paddingHorizontal: 16,
  },
  pickerCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 16,
    paddingVertical: 16,
    paddingHorizontal: 18,
    marginBottom: 14,
  },
  // Animated "tap here" nudge on the first card.
  tapNudge: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 6,
  },
  tapNudgeRipple: {
    position: 'absolute',
    width: 34,
    height: 34,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.55)',
  },
  pickerAvatarLg: {
    width: 46,
    height: 46,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.24)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  pickerAvatarRow: {
    width: 44,
    height: 44,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.24)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  pickerRowChevron: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // ── Conversation header ──
  convHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  backBtn: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: CCColors.White,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: CCColors.BorderLight,
  },
  convAvatar: {
    width: 40,
    height: 40,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  switcherWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  switcherLabel: {
    letterSpacing: 0.6,
  },
  // No shared background pill — the dots are individual outlined chips.
  switcher: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  switcherDot: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  switcherDotHover: {
    transform: [{scale: 1.15}],
  },
  switcherLock: {
    position: 'absolute',
    bottom: -3,
    right: -3,
    width: 14,
    height: 14,
    borderRadius: 999,
    backgroundColor: CCColors.TextColor.Dark,
    alignItems: 'center',
    justifyContent: 'center',
  },
  switcherTooltip: {
    position: 'absolute',
    bottom: '128%',
    right: 0,
    backgroundColor: CCColors.TextColor.Dark,
    borderRadius: 8,
    paddingVertical: 5,
    paddingHorizontal: 9,
    zIndex: 40,
  },

  // ── Human / WhatsApp card ──
  humanCard: {
    backgroundColor: CCColors.White,
    borderRadius: 16,
    padding: 28,
    alignItems: 'center',
  },
  humanBadge: {
    width: 52,
    height: 52,
    borderRadius: 999,
    backgroundColor: '#25D366',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  waBtn: {
    backgroundColor: '#25D366',
    borderRadius: 999,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // ── Starters ("Try asking") ──
  eyebrow: {letterSpacing: 1, marginBottom: 12},
  starterCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: CCColors.White,
    borderWidth: 1.5,
    borderColor: CCColors.BorderLight,
    borderRadius: 10,
    paddingVertical: 15,
    paddingHorizontal: 18,
    marginBottom: 10,
  },
  starterArrow: {
    width: 26,
    height: 26,
    borderRadius: 999,
    backgroundColor: CCColors.Primary.Default,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginLeft: 10,
  },

  // ── Messages ──
  msgRow: {width: '100%', marginVertical: 5},
  bubble: {
    maxWidth: '82%',
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  msgMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '82%',
    marginTop: 4,
  },

  // ── Input pill ──
  inputPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CCColors.White,
    borderRadius: 999,
    paddingLeft: 20,
    paddingRight: 8,
    paddingVertical: 8,
    borderWidth: 1.5,
    borderColor: '#D1AB41', // yellowDark — a warm highlight ring
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 999,
    backgroundColor: CCColors.Primary.Default,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // ── Locked (non-CSOC) card ──
  lockBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 26,
    height: 26,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.9)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  // Fixed tooltip, centred over the locked card.
  lockTooltipFloat: {
    position: 'absolute',
    left: 12,
    right: 12,
    top: '50%',
    transform: [{translateY: -28}],
    backgroundColor: CCColors.TextColor.Dark,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    zIndex: 40,
  },

  // ── Picker footer: Caissa waving, sticky to the panel bottom ──
  caissaFooter: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  caissaFooterInner: {
    width: 240,
    height: 200,
  },

  // ── Psychology quick chips (kept for parity) ──
  psychologyChip: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: CCColors.LightBrownShade,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    minWidth: 0,
  },
});

export default styles;
